import {
  InternalPayItemConnectionClassType,
  alphabeticalCompare,
} from '@cims-online/shared'
import {
  PayItemConnectionGroupsQuery,
  usePayItemConnectionGroupsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import {
  LabeledValueType,
  DefaultOptionType,
} from 'components/CIMSSelect/types'
import { useCallback, useMemo } from 'react'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { useProjectContext } from 'context/ProjectContext'
import { decodeConnectionValues } from './utils'
import { usePayItemsModuleContext } from '../../../../PayItemsModuleContext'
import { WorkflowConnection } from '../../../../PayItemsModuleContext/types'

type QueryGroup =
  PayItemConnectionGroupsQuery['project']['payItemConnectionGroups'][0]
export type PayItemConnectionGroup = QueryGroup & {
  workflowConnections: WorkflowConnection[]
}

type Connection =
  PayItemConnectionGroupsQuery['project']['payItemConnections'][number]

type GroupsContext = {
  refetch: () => Promise<void>
  groups: PayItemConnectionGroup[] | undefined
  fetching: boolean
  connectionOptions: DefaultOptionType[]
  toConnectionValues: (options: {
    connections: Connection[]
    internalIds: string[]
    workflowConnections: WorkflowConnection[]
  }) => LabeledValueType[]
  fromConnectionValues: (values: LabeledValueType[]) => {
    connectionIds: string[]
    internalConnectionIds: string[]
    workflowIds: string[]
  }
}

const { Provider, useContextFn } =
  createStrictContext<GroupsContext>('GroupsContext')

export const useGroupsContext = useContextFn

const GroupsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    fetching: fetchingPayItems,
    refetch: refetchPayItems,
    workflowConnections,
  } = usePayItemsModuleContext()
  const {
    data,
    loading: fetchingGroups,
    refetch: refetchGroups,
  } = usePayItemConnectionGroupsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const connectionLabel = ({ name, googleSheet }: Connection) =>
    name ??
    (googleSheet ? googleSheet?.title ?? googleSheet.compositeId : 'Unknown')
  const connections = useOptions({
    items: data?.project?.payItemConnections,
    value: ({ id, __typename }) => `${__typename}_${id}`,
    label: connectionLabel,
    tooltip: ({ googleSheet }) => (googleSheet ? 'Google Sheet' : undefined),
    sort: (a, b) => {
      const A = connectionLabel(a)
      const B = connectionLabel(b)
      return alphabeticalCompare(A, B)
    },
  })
  const internals = useOptions({
    items: InternalPayItemConnectionClassType.values,
    value: ({ id }) => `internal_${id}`,
    label: ({ module }) => module.name,
    sort: (a, b) => alphabeticalCompare(a.module, b.module, 'name'),
  })
  const workflows = useOptions({
    items: workflowConnections,
    value: ({ workflowId }) => `workflow_${workflowId}`,
    label: ({ workflow }) => workflow.label,
    tooltip: () => 'Workflow',
    sort: (a, b) => alphabeticalCompare(a.workflow, b.workflow, 'label'),
  })
  const groups = useMemo(
    () =>
      data?.project?.payItemConnectionGroups.map((group) => ({
        ...group,
        workflowConnections: workflowConnections.filter(
          ({ groupId }) => groupId === group.id
        ),
      })),
    [data?.project?.payItemConnectionGroups, workflowConnections]
  )
  const refetch = useCallback(async () => {
    await Promise.all([refetchGroups(), refetchPayItems()])
  }, [refetchGroups, refetchPayItems])
  const fetching = fetchingGroups || Boolean(fetchingPayItems)
  const toConnectionValues = useCallback<GroupsContext['toConnectionValues']>(
    ({ connections, internalIds, workflowConnections }) =>
      toLabeledValues(
        [...connections, ...internalIds, ...workflowConnections],
        {
          type: 'callback',
          callback: (item) => {
            if (typeof item === 'string')
              return {
                value: `internal_${item}`,
                label:
                  InternalPayItemConnectionClassType.fromIdOrFail(item).module
                    .name,
                tooltip: 'Workflow',
              }
            if (item.__typename === 'WorkflowPayItemSummaryConnection')
              return {
                value: `workflow_${item.workflowId}`,
                label: item.workflow.label,
                tooltip: 'Workflow',
              }
            const { id, __typename, googleSheet, name } = item
            return {
              value: `${__typename}_${id}`,
              label:
                name ??
                (googleSheet
                  ? googleSheet?.title ?? googleSheet.compositeId
                  : 'Unknown'),
              tooltip: googleSheet ? 'Google Sheet' : undefined,
            }
          },
        }
      ),
    []
  )
  const fromConnectionValues = useCallback<
    GroupsContext['fromConnectionValues']
  >((values) => decodeConnectionValues(values), [])
  return (
    <Provider
      value={{
        groups,
        fetching,
        refetch,
        connectionOptions: [
          { value: 'connections', label: 'Connections', options: connections },
          {
            value: 'workflows',
            label: 'Workflows',
            options: [...internals, ...workflows],
          },
        ],
        toConnectionValues,
        fromConnectionValues,
      }}
    >
      {children}
    </Provider>
  )
}

export default GroupsProvider
